import { useState } from 'react';

import { arrayMoveImmutable } from 'array-move';
import {
  Container as DragAndDropContainer,
  DropResult,
} from 'react-smooth-dnd';

import { DocumentDto } from '@cfa/pathway-fe-shared/xp';

import {
  IntlSubcategoryDTO,
  IntlSubtitleDTO,
} from '@/services/content-api-types';

import Subtitle from '@/routes/Admin/components/Subtitle';
import { setAdminState } from '@/routes/Admin/state';

interface SubtitleDragAndDropProps {
  adminCategoryId: string;
  index: number;
  onSaveSubtitlesOrder: (
    updatedSubtitles: IntlSubtitleDTO[],
    index: number,
  ) => void;
  openInNewTab: boolean;
  selectedCountry: string;
  showAdminFunctionality: boolean;
  subtitles: IntlSubtitleDTO[] | undefined;
  userIsAdmin: boolean;
  sectionEditType: 'add' | 'edit' | undefined;
  setSectionEditType: (type: 'add' | 'edit' | undefined) => void;
  subcategory: IntlSubcategoryDTO;
}

const SubtitleDragAndDrop: React.FC<SubtitleDragAndDropProps> = ({
  index,
  onSaveSubtitlesOrder,
  openInNewTab,
  selectedCountry,
  showAdminFunctionality,
  subtitles,
  userIsAdmin,
  sectionEditType,
  setSectionEditType,
  subcategory,
}) => {
  const [mutableSubtitles, setMutableSubtitles] = useState(subtitles ?? []);

  const [prevSubtitles, setPrevSubtitles] = useState(subtitles);
  if (prevSubtitles !== subtitles) {
    setPrevSubtitles(subtitles);
    if (subtitles) {
      setMutableSubtitles(subtitles);
    }
  }

  const updateArrayIndex = (
    array: IntlSubtitleDTO[],
    removedIndex: number,
    addedIndex: number,
  ) => {
    if (removedIndex < 0 || addedIndex < 0) {
      return;
    }

    return arrayMoveImmutable(array, removedIndex, addedIndex).map(item => ({
      ...item,
    }));
  };

  const onDrop = (dropResult: DropResult) => {
    if (
      dropResult.removedIndex === null ||
      dropResult.removedIndex === dropResult.addedIndex ||
      dropResult.addedIndex === null
    ) {
      return;
    }
    const { removedIndex, addedIndex } = dropResult;

    const updatedSubtitles = updateArrayIndex(
      mutableSubtitles,
      removedIndex,
      addedIndex,
    );
    if (updatedSubtitles) {
      setMutableSubtitles(updatedSubtitles);
      onSaveSubtitlesOrder(updatedSubtitles, index);
    }
  };

  const handleAdminAddResourceToNewSubtitle = (
    subcategoryIndex: number,
    subtitleIndex: number,
  ) => {
    setAdminState('selectedSubcategoryIndex', subcategoryIndex);
    setAdminState('selectedSubtitleIndex', subtitleIndex);
    setAdminState('showAddProcedurePopUp', true);
  };

  const deleteDocument = (
    subcategoryIndex: number,
    subtitleIndex: number,
    document: DocumentDto,
    documentIsMarkedAsVisible: boolean,
    documentIndex: number,
  ) => {
    setAdminState('selectedSubcategoryIndex', subcategoryIndex);
    setAdminState('selectedSubtitleIndex', subtitleIndex);
    setAdminState('selectedDocument', document);
    setAdminState('selectedDocumentIndex', documentIndex);
    setAdminState('documentIsVisibleInSubtitle', documentIsMarkedAsVisible);
    setAdminState('confirmationModalType', 'documentDelete');
  };

  const toggleDocument = (
    subcategoryIndex: number,
    subtitleIndex: number,
    document: DocumentDto,
    documentIsMarkedAsVisible: boolean,
  ) => {
    setAdminState('selectedSubcategoryIndex', subcategoryIndex);
    setAdminState('selectedSubtitleIndex', subtitleIndex);
    setAdminState('selectedDocument', document);

    setAdminState('documentIsVisibleInSubtitle', documentIsMarkedAsVisible);
    setAdminState('confirmationModalType', 'documentVisibility');
  };

  return !sectionEditType ? (
    <div className="border-t-grayscale-gray2 w-full border-t pt-4">
      {/** @ts-expect-error it's yelling about children but we need children for the drag and drop */}
      <DragAndDropContainer
        className="StyledDragAndDropContainer"
        dragHandleSelector=".subtitle-drag-handle"
        onDrop={e => onDrop(e)}
      >
        {mutableSubtitles.map((mutableSubtitle, subtitleIndex) => (
          <Subtitle
            key={`subtitle-${mutableSubtitle.id}-${subtitleIndex}`}
            countryName={selectedCountry}
            handleDocumentDelete={(
              document,
              documentIsMarkedAsVisible,
              documentIndex,
            ) => {
              deleteDocument(
                index,
                subtitleIndex,
                document,
                documentIsMarkedAsVisible,
                documentIndex,
              );
            }}
            handleToggleDocument={(document, documentIsMarkedAsVisible) =>
              toggleDocument(
                index,
                subtitleIndex,
                document,
                documentIsMarkedAsVisible,
              )
            }
            onAdminAddResourceToSubtitle={() =>
              handleAdminAddResourceToNewSubtitle(index, subtitleIndex)
            }
            onSaveSubtitlesOrder={onSaveSubtitlesOrder}
            openInNewTab={openInNewTab}
            showAdminFunctionality={showAdminFunctionality}
            showSubtitleEditFunctionality={true}
            subcategory={subcategory}
            subcategoryIndex={index}
            subtitle={mutableSubtitle}
            subtitleIndex={subtitleIndex}
            subtitles={mutableSubtitles}
            userIsAdmin={userIsAdmin}
            sectionEditType={sectionEditType}
            setSectionEditType={setSectionEditType}
          />
        ))}
      </DragAndDropContainer>
    </div>
  ) : null;
};

export default SubtitleDragAndDrop;
